import axios, { AxiosInstance } from 'axios';
import { 
  ReleaseNoteItem, 
  ReleaseNoteItemFilter, 
  PagedResult, 
  Release,
  ReleaseType,
  ModuleMapping,
  QueryFilter,
  Term,
  Subscriber,
  PublishRequest,
  PublishResponse,
  UpdateReleaseRequest,
  UpdateReleaseResponse,
} from '@/types';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';

class ApiService {
  private client: AxiosInstance;
  private accessToken: string | null = null;

  constructor() {
    this.client = axios.create({
      baseURL: API_BASE_URL,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Add auth interceptor
    this.client.interceptors.request.use((config) => {
      if (this.accessToken) {
        config.headers.Authorization = `Bearer ${this.accessToken}`;
      }
      return config;
    });
  }

  setAccessToken(token: string | null) {
    this.accessToken = token;
  }

  // Release Note Items
  async getReleaseNoteItems(filter?: ReleaseNoteItemFilter, page = 1, pageSize = 100): Promise<PagedResult<ReleaseNoteItem>> {
    const params = new URLSearchParams();
    if (filter?.productSuite) params.append('productSuite', filter.productSuite);
    if (filter?.status !== undefined) params.append('status', filter.status.toString());
    if (filter?.searchTerm) params.append('searchTerm', filter.searchTerm);
    if (filter?.excludePublished) params.append('excludePublished', 'true');
    if (filter?.excludeExcluded) params.append('excludeExcluded', 'true');
    if (filter?.issueTypes?.length) params.append('issueTypes', filter.issueTypes.join(','));
    if (filter?.generatedAfter) params.append('generatedAfter', filter.generatedAfter);
    if (filter?.generatedBefore) params.append('generatedBefore', filter.generatedBefore);
    params.append('page', page.toString());
    params.append('pageSize', pageSize.toString());

    const response = await this.client.get<PagedResult<ReleaseNoteItem>>(`/items?${params}`);
    return response.data;
  }

  async getReleaseNoteItemStats(): Promise<{ editedTicketCount: number }> {
    const response = await this.client.get<{ editedTicketCount: number }>('/manage/items/stats');
    return response.data;
  }

  async getReleaseNoteItem(id: string): Promise<ReleaseNoteItem> {
    const response = await this.client.get<ReleaseNoteItem>(`/items/${id}`);
    return response.data;
  }

  async updateReleaseNoteItem(item: ReleaseNoteItem): Promise<ReleaseNoteItem> {
    const response = await this.client.put<ReleaseNoteItem>(`/items/${item.id}`, item);
    return response.data;
  }

  async regenerateReleaseNote(id: string): Promise<ReleaseNoteItem> {
    const response = await this.client.post<ReleaseNoteItem>(`/items/${id}/regenerate`);
    return response.data;
  }

  async excludeItem(id: string): Promise<ReleaseNoteItem> {
    const response = await this.client.post<ReleaseNoteItem>(`/items/${id}/exclude`);
    return response.data;
  }

  async includeItem(id: string): Promise<ReleaseNoteItem> {
    const response = await this.client.post<ReleaseNoteItem>(`/items/${id}/include`);
    return response.data;
  }

  // Releases
  async getReleases(): Promise<Release[]> {
    // Use manage endpoint which is protected by Azure AD (not Upshop JWT)
    const response = await this.client.get<Release[]>('/manage/releases');
    return response.data;
  }

  async getRelease(id: string): Promise<Release> {
    // Use manage endpoint which is protected by Azure AD (not Upshop JWT)
    const response = await this.client.get<Release>(`/manage/release/${id}`);
    return response.data;
  }

  async getLatestRelease(): Promise<Release | null> {
    const response = await this.client.get<Release | null>('/releases/latest');
    return response.data;
  }

  async publishRelease(request: PublishRequest): Promise<PublishResponse> {
    const response = await this.client.post<PublishResponse>('/releases/publish', request);
    return response.data;
  }

  async previewReleaseEmail(request: PublishRequest): Promise<string> {
    const response = await this.client.post('/releases/preview-email', request, {
      responseType: 'text',
      headers: { 'Accept': 'text/html' }
    });
    return response.data;
  }

  async updateRelease(id: string, request: UpdateReleaseRequest): Promise<UpdateReleaseResponse> {
    const response = await this.client.put<UpdateReleaseResponse>(`/releases/${id}`, request);
    return response.data;
  }

  // Release Types
  async getReleaseTypes(): Promise<ReleaseType[]> {
    const response = await this.client.get<ReleaseType[]>('/release-types');
    return response.data;
  }

  async createReleaseType(releaseType: Partial<ReleaseType>): Promise<ReleaseType> {
    const response = await this.client.post<ReleaseType>('/release-types', releaseType);
    return response.data;
  }

  async updateReleaseType(releaseType: ReleaseType): Promise<ReleaseType> {
    const response = await this.client.put<ReleaseType>(`/release-types/${releaseType.id}`, releaseType);
    return response.data;
  }

  async deleteReleaseType(id: string): Promise<void> {
    await this.client.delete(`/release-types/${id}`);
  }

  // Module Mappings
  async getModuleMappings(): Promise<ModuleMapping[]> {
    const response = await this.client.get<ModuleMapping[]>('/module-mappings');
    return response.data;
  }

  async createModuleMapping(mapping: Partial<ModuleMapping>): Promise<ModuleMapping> {
    const response = await this.client.post<ModuleMapping>('/module-mappings', mapping);
    return response.data;
  }

  async updateModuleMapping(mapping: ModuleMapping): Promise<ModuleMapping> {
    const response = await this.client.put<ModuleMapping>(`/module-mappings/${mapping.id}`, mapping);
    return response.data;
  }

  async deleteModuleMapping(id: string): Promise<void> {
    await this.client.delete(`/module-mappings/${id}`);
  }

  // Terms (glossary)
  async getTerms(): Promise<Term[]> {
    const response = await this.client.get<Term[]>('/terms');
    return response.data;
  }

  async createTerm(term: Partial<Term>): Promise<Term> {
    const response = await this.client.post<Term>('/terms', term);
    return response.data;
  }

  async updateTerm(term: Term): Promise<Term> {
    const response = await this.client.put<Term>(`/terms/${term.id}`, term);
    return response.data;
  }

  async deleteTerm(id: string): Promise<void> {
    await this.client.delete(`/terms/${id}`);
  }

  async getProductSuites(): Promise<string[]> {
    const response = await this.client.get<string[]>('/module-mappings/product-suites');
    return response.data;
  }

  // Query Filters
  async getQueryFilters(): Promise<QueryFilter[]> {
    const response = await this.client.get<QueryFilter[]>('/query-filters');
    return response.data;
  }

  async createQueryFilter(filter: Partial<QueryFilter>): Promise<QueryFilter> {
    const response = await this.client.post<QueryFilter>('/query-filters', filter);
    return response.data;
  }

  async updateQueryFilter(filter: QueryFilter): Promise<QueryFilter> {
    const response = await this.client.put<QueryFilter>(`/query-filters/${filter.id}`, filter);
    return response.data;
  }

  async deleteQueryFilter(id: string): Promise<void> {
    await this.client.delete(`/query-filters/${id}`);
  }

  // Subscribers (Azure AD protected endpoints)
  async getSubscribers(): Promise<Subscriber[]> {
    const response = await this.client.get<Subscriber[]>('/subscribers');
    return response.data;
  }

  async addSubscribers(emails: string[], addedBy?: string): Promise<{ added: number }> {
    const response = await this.client.post<{ added: number; message: string }>(
      '/subscribers', 
      {
        emails,
        addedBy,
      }
    );
    return response.data;
  }

  async removeSubscriber(email: string, deletedBy?: string): Promise<void> {
    const params = deletedBy ? `?deletedBy=${encodeURIComponent(deletedBy)}` : '';
    await this.client.delete(`/subscribers/${encodeURIComponent(email)}${params}`);
  }

  // Import releases from releasenotes.io
  async importReleases(): Promise<{ imported: number }> {
    const response = await this.client.post<{ imported: number }>('/releases/import');
    return response.data;
  }
}

export const api = new ApiService();
export default api;
